/**
 * Filliers age gate (Figma `overlay-age` 771:4702).
 *
 * The visitor types a birth year; if it clears the minimum age the gate writes a cookie and
 * removes itself. Two cookie lifetimes, one name: with "remember me" ticked it gets a
 * Max-Age from the theme setting, without it none at all, which makes it a session cookie
 * the browser drops on quit. Setting that lifetime to **0** ("Tot de browser sluit") takes the
 * checkbox away entirely, so every visitor gets the session cookie — see `pass()`.
 *
 * A cookie and not sessionStorage/localStorage. For the theme alone either would do — the
 * gate only ever runs on pages this layout renders. The cookie is chosen because it is the
 * one form a *future* check could also read: a server-side or app-side gate (§17-11 is still
 * open) sees cookies on the request, where web storage never leaves the page.
 *
 * Whether the gate shows at all is decided *before* this file runs, by the blocking script
 * in `layout/theme.liquid` that puts `fil-age-ask` on `<html>` when the cookie is absent —
 * see the comment there. This element only reads that class back to know which state it woke
 * up in, so the cookie is parsed once per page and not twice.
 *
 * **This file is not load-bearing for reachability (9/9).** It used to be: the stylesheet had
 * the gate visible and only this script could remove it, so failing to load meant a
 * full-viewport blocker with no working form behind it. The default is now hidden and the
 * gate is opened deliberately, and the watchdog in `snippets/filliers-age-gate.liquid`
 * watches for the `fil-age-live` class this file sets once the form is wired. If it never
 * appears, the gate comes down. So a broken build costs the gate, not the storefront.
 */

const AGE_COOKIE = 'filliers_age_ok';

class FilliersAgeGate extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('.filliers-age-gate__input');
    this.error = this.querySelector('.filliers-age-gate__error');
    this.remember = this.querySelector('input[name="remember"]');
    if (!this.form || !this.input) return;

    this.minAge = parseInt(this.dataset.minAge, 10) || 18;

    /*
     * Not `|| 365`. Since 16/9 (FIL001-12 §10) the setting has a **zero** option — "Tot de browser
     * sluit" — and zero is falsy, so the old fallback turned the shortest lifetime into the longest
     * one silently. `Number.isNaN` separates "not set" from "set to zero", which is the whole
     * distinction the new option introduces.
     */
    const days = parseInt(this.dataset.rememberDays, 10);
    this.rememberDays = Number.isNaN(days) ? 365 : days;

    // In the theme editor the box renders so the merchant can see the copy they are editing,
    // but it must not trap focus, lock scrolling or write a cookie — otherwise editing the
    // rest of the page means answering a gate first, and answering it once would hide it
    // from the editor for a year.
    //
    // Read *before* the already-answered check since 9/9, not after. The cookie belongs to
    // the browser, so a merchant who answered on the storefront used to hit that early bail
    // in the editor too and got a box with a dead form in it. The stylesheet shows the box
    // in design mode either way, so the only thing the old order achieved was making it
    // inert.
    this.isPreview = 'designMode' in this.dataset;

    // Already answered: the stylesheet keeps the box hidden, so there is nothing to open and
    // nothing to trap. Bailing here is what keeps a returning visitor's page scrollable.
    // Inverted with the class 9/9 — `fil-age-ask` present means the answer is still owed.
    if (!this.isPreview && !document.documentElement.classList.contains('fil-age-ask')) return;

    this.form.addEventListener('submit', this.onSubmit.bind(this));
    // Clears a stale message as soon as the visitor starts fixing the answer, rather than
    // leaving "vul een geldig geboortejaar in" under a field that now holds a valid year.
    this.input.addEventListener('input', this.clearError.bind(this));

    /*
     * "The form works" — the one thing the watchdog in `snippets/filliers-age-gate.liquid`
     * is waiting for. Set here rather than at the top of this method on purpose: it must
     * mean *the visitor can get out*, so it goes after the submit handler is attached and
     * after every early return above that would leave the box unusable. If this line is
     * never reached, the watchdog takes the gate down instead of leaving it standing.
     */
    document.documentElement.classList.add('fil-age-live');

    if (this.isPreview) return;

    document.documentElement.classList.add('fil-age-gate-open');
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Focus the field, not the box: a dialog whose first stop is the thing you have to fill
    // in saves a keyboard visitor a Tab, and the heading is already the accessible name via
    // aria-labelledby.
    this.input.focus({ preventScroll: true });
  }

  onSubmit(event) {
    event.preventDefault();

    const year = parseInt(this.input.value.trim(), 10);
    const thisYear = new Date().getFullYear();

    // Four digits, not in the future, and not so far back that it is a typo rather than a
    // person. 1900 is the floor because a 126-year-old visitor is a slip of the hand.
    if (!/^\d{4}$/.test(this.input.value.trim()) || year > thisYear || year < 1900) {
      this.showError(this.error.dataset.invalid);
      return;
    }

    /*
     * Year arithmetic, which is all the frame's field can support: someone who turns 18
     * later this year passes today. Asking for a full date would close that gap, but the
     * design draws a YYYY field and §16b is not ours to reinterpret — the mismatch between
     * the label ("date of birth") and the field is recorded in QUESTIONS-TO-ASK instead.
     */
    if (thisYear - year < this.minAge) {
      this.showError(this.error.dataset.denied);
      return;
    }

    if (this.isPreview) {
      this.showError('');
      return;
    }

    this.pass();
  }

  pass() {
    const remembered = this.remember ? this.remember.checked : false;

    /*
     * `1` rather than the year the visitor typed. The answer we need is "old enough", and
     * writing a birth year into a cookie that every script on the page can read would be
     * storing personal data we have no use for.
     */
    const parts = [`${AGE_COOKIE}=1`, 'path=/', 'samesite=lax'];
    /*
     * `rememberDays > 0` as well as the tick. With the lifetime set to "Tot de browser sluit" the
     * snippet already withholds the checkbox, so `remembered` is false and this never fires — the
     * second half of the test is belt and braces, and it is what makes the setting authoritative
     * rather than dependent on the checkbox being absent. No Max-Age means a session cookie, which
     * the browser drops when the last tab closes.
     */
    if (remembered && this.rememberDays > 0) parts.push(`max-age=${this.rememberDays * 24 * 60 * 60}`);
    // Shopify serves the storefront over HTTPS everywhere, but `shopify theme dev` is
    // http://127.0.0.1 — a Secure cookie there would be dropped and the gate would ask on
    // every reload while previewing.
    if (window.location.protocol === 'https:') parts.push('secure');
    document.cookie = parts.join('; ');

    // Dropping `fil-age-ask` is what hides the gate now (it used to *add* `fil-age-ok`).
    // `this.remove()` below would be enough on its own, but the class is also what a
    // re-rendered section or a bfcache restore would be read back against, so both go.
    document.documentElement.classList.remove('fil-age-ask');
    document.documentElement.classList.remove('fil-age-gate-open');
    this.remove();
  }

  showError(message) {
    this.error.textContent = message || '';
    this.input.setAttribute('aria-invalid', message ? 'true' : 'false');
    if (message) this.input.focus({ preventScroll: true });
  }

  clearError() {
    if (this.error.textContent) this.showError('');
  }

  /**
   * A focus trap and nothing else — no Escape handler, on purpose. Escape closes a dialog,
   * and this one has no dismissed state: the design draws no close button because there is
   * nowhere to go until the question is answered.
   *
   * Written against the element's own focusables rather than Dawn's `Garnish`-style trap
   * (`global.js` has `trapFocus`, but it also removes the trap on Escape, which is the one
   * behaviour we must not have).
   */
  onKeydown(event) {
    if (event.key !== 'Tab') return;

    const focusable = this.querySelectorAll('input, button, a[href], [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('filliers-age-gate')) {
  customElements.define('filliers-age-gate', FilliersAgeGate);
}
